/**
 * „Podgląd · co zobaczy klient" (T-B1) — the client's booking screen on the client's phone, drawn
 * from the trainer's own hours and policy (`useClientPreview`, `model/clientPreview`).
 *
 * The phone is `shared/ui/PhoneFrame`, the same frame and the same client-app colours `/konto`
 * uses — theme-independent, where the T-B1 canon's phone follows the trainer's theme: it is the
 * CLIENT's device. It is decoration to a screen reader (one sentence says what it shows) and the
 * phone stands from the first frame; only its screen waits, as bars in the picker's own shape.
 *
 * Nobody in particular is booking here, so the two things a client's screen shows about that
 * client — „Jak ostatnio" (their last booking) and the credit line under the CTA — are not drawn.
 */

import { useTranslation } from 'react-i18next';

import { useSessionUser } from '~/entities/session';
import { PhoneFrame } from '~/shared/ui/PhoneFrame';
import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { ClientPreviewState } from '../lib/useClientPreview';
import {
  longWeekday,
  dayMonthWords,
  weekdayDayMonth,
  weekdayDayMonthClock,
} from '../model/calendarWords';
import { clockOf, wallClock } from '../model/trainerClock';
import { WEEKDAYS } from '../model/weekPattern';
import styles from './ClientPhone.module.css';

interface ClientPhoneProps {
  readonly state: ClientPreviewState;
  readonly zone: string;
}

const DENSITY_CLASS = {
  many: styles.cellMany,
  few: styles.cellFew,
  none: styles.cellNone,
  past: styles.cellPast,
} as const;

export function ClientPhone({ state, zone }: ClientPhoneProps) {
  const { t } = useTranslation();
  return (
    <aside className={styles.column} aria-labelledby="booking-preview-eyebrow">
      <div className={styles.caption}>
        <h2 id="booking-preview-eyebrow" className={styles.eyebrow}>
          {t('web:booking.settings.preview.eyebrow')}
        </h2>
        <span className={styles.source}>{t('web:booking.settings.preview.source')}</span>
      </div>
      <span className={hidden.srOnly}>{t('web:booking.settings.preview.alt')}</span>
      <PhoneFrame className={styles.phone}>
        <Screen state={state} zone={zone} />
      </PhoneFrame>
    </aside>
  );
}

function WaitingScreen({ skeleton: drawn }: { readonly skeleton: boolean }) {
  const bar = (className: string | undefined) => (
    <span className={`${String(className)} ${drawn ? String(skeleton.bar) : ''}`} />
  );
  return (
    <div className={styles.body}>
      {bar(styles.barTitle)}
      {bar(styles.barMonth)}
      {bar(styles.barGrid)}
      {bar(styles.barHours)}
    </div>
  );
}

function Screen({ state, zone }: ClientPhoneProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const user = useSessionUser();
  const { gate, preview, durationMinutes } = state;
  const trainer = (user?.displayName ?? '').split(' ')[0] ?? '';
  const months = t('calendar.monthsNominative', { returnObjects: true });
  const monthNames = Array.isArray(months) ? (months as string[]) : [];

  if (preview === null) return <WaitingScreen skeleton={gate.skeleton} />;

  return (
    <div className={`${String(styles.body)} ${gate.reveal}`}>
      <div className={styles.head}>
        <span className={styles.back}>
          <ChevronGlyph className={styles.glyph} direction="left" />
        </span>
        <div className={styles.headText}>
          <span className={styles.kicker}>
            {`${trainer} · ${t('web:booking.settings.preview.duration', { count: durationMinutes })}`}
          </span>
          <span className={styles.title}>{t('web:booking.client.pick.titleByType.Training')}</span>
        </div>
        <span className={styles.types}>
          <span className={`${String(styles.type)} ${String(styles.typeOn)}`}>
            {t('web:booking.client.type.Training')}
          </span>
          <span className={styles.type}>{t('web:booking.client.type.Consultation')}</span>
        </span>
      </div>

      {preview.kind === 'failed' ? (
        <p className={styles.message}>{t('web:booking.settings.preview.unread')}</p>
      ) : preview.kind === 'off' ? (
        <div className={styles.message}>
          <b className={styles.messageTitle}>
            {t('web:booking.client.refusal.self_booking_disabled.title', { trainer })}
          </b>
          {t('web:booking.client.refusal.self_booking_disabled.body', { trainer })}
        </div>
      ) : preview.kind === 'noZone' ? (
        <div className={styles.message}>
          <b className={styles.messageTitle}>{t('web:booking.client.pick.emptyTitle')}</b>
          {t('web:booking.schedule.zoneUnsetLine')}
        </div>
      ) : preview.kind === 'empty' ? (
        <div className={styles.message}>
          <b className={styles.messageTitle}>{t('web:booking.client.pick.emptyTitle')}</b>
          {t('web:booking.client.pick.emptyBody')}
        </div>
      ) : (
        <>
          <div className={styles.month}>
            <span className={styles.monthName}>
              {monthNames[Number(preview.month.slice(5, 7)) - 1] ?? ''}{' '}
              <em className={styles.year}>{preview.month.slice(0, 4)}</em>
            </span>
            <span className={styles.arrows}>
              <i className={styles.arrow}>
                <ChevronGlyph className={styles.glyph} direction="left" />
              </i>
              <i className={styles.arrow}>
                <ChevronGlyph className={styles.glyph} direction="right" />
              </i>
            </span>
          </div>
          <div className={styles.weekdays}>
            {WEEKDAYS.map((weekday) => (
              <span key={weekday}>{t(`web:weekdays.short.${String(weekday)}`)}</span>
            ))}
          </div>
          <div className={styles.grid}>
            {preview.weeks.flat().map((cell, index) => (
              <span
                key={cell.day ?? `blank-${String(index)}`}
                className={`${String(styles.cell)} ${cell.day === null ? String(styles.cellBlank) : String(DENSITY_CLASS[cell.density])} ${cell.selected ? String(styles.cellOn) : ''}`}
              >
                {cell.day === null ? '' : String(Number(cell.day.slice(8, 10)))}
              </span>
            ))}
          </div>
          <div className={styles.legend}>
            <span>
              <b>12</b> {t('web:booking.client.pick.legendMany')}
            </span>
            <span>
              <b className={styles.legendFew}>12</b> {t('web:booking.client.pick.legendFew')}
            </span>
            <span>
              <b className={styles.legendNone}>12</b> {t('web:booking.client.pick.legendNone')}
            </span>
          </div>
          <div className={styles.dayHead}>
            <b>
              {t('web:booking.client.pick.dayHead', {
                weekday: longWeekday(preview.day, locale),
                date: dayMonthWords(preview.day, locale),
              })}
            </b>
            <span>{t('web:booking.client.pick.hoursCount', { count: preview.starts.length })}</span>
          </div>
          <div className={styles.hours}>
            {preview.starts.map((start) => (
              <span
                key={start.startMs}
                className={`${String(styles.hour)} ${start.startMs === preview.start.startMs ? String(styles.hourOn) : ''}`}
              >
                {clockOf(start.minutes)}
              </span>
            ))}
          </div>
          <div className={styles.foot}>
            <div className={styles.footText}>
              <span className={styles.footLine}>
                {t('web:booking.client.sessionLine', {
                  type: t('web:booking.client.type.Training'),
                  duration: t('web:booking.settings.preview.duration', { count: durationMinutes }),
                  mode: t('web:booking.client.stationary'),
                })}
              </span>
              <b className={styles.footWhen}>
                {`${weekdayDayMonth(preview.day, locale)}, ${wallClock(preview.start.startMs, zone)}–${wallClock(preview.endMs, zone)}`}
              </b>
              {preview.cancelFreeUntilMs === null ? null : (
                <span className={styles.footLine}>
                  {t('web:booking.client.pick.freeCancelUntil', {
                    moment: weekdayDayMonthClock(preview.cancelFreeUntilMs, zone, locale),
                  })}
                </span>
              )}
            </div>
            <span className={styles.cta}>{t('web:booking.client.pick.cta')}</span>
          </div>
        </>
      )}
    </div>
  );
}
